import { Space, message, Button, notification } from 'antd';
import { UpSquareOutlined } from '@ant-design/icons';
import { useEffect } from 'react';
import { BroadcastSync } from 'react-simple-channel';

import { VERSION_REFRESH_SYNC_CHANNEL } from '@/constants/broadcast-channel';
import { isPro } from '@/utils/utils';

// 获取一个版本更新后的通知处理器
const getNotificationEventHandler = (wait: number) => {
  const notificationHandler = async () => {
    const now = Date.now();
    const res = await fetch(`/?now=${Date.now()}`, {
      headers: {
        'Cache-Control': 'no-cache',
      },
    });

    const html = await res.text();

    const match = html.match(/<meta name="buildTime" content="(.*?)">/);

    const newBuildTime = match?.[1] || '';

    // 插入一个后门，方便 QA 测试
    // 使用 URL 参数中获取 __buildTime__ 的值 来作为当前首页中 meta 标签中的 buildTime
    // ================================================
    const { search } = window.location;
    const searchBuildTime =
      search.includes('__buildTime__') && search.match(/__buildTime__=([^&]+)/)?.[1];
    // ================================================

    const oldBuildTime =
      searchBuildTime ||
      window.document?.querySelector?.('meta[name="buildTime"]')?.getAttribute?.('content') ||
      '';

    if (newBuildTime !== oldBuildTime) {
      notification.open({
        message: '系统版本更新通知',
        key: oldBuildTime!,
        description: '检测到系统有新版本发布，是否立即刷新页面来获取最新内容？',
        icon: <UpSquareOutlined style={{ color: '#5F49E5' }} />,
        btn: (
          <Space size={8}>
            <Button
              style={{
                color: '#000',
                background: '#fff',
                borderColor: '#d9d9d9',
              }}
              onClick={() => notification.destroy(oldBuildTime!)}
            >
              稍后再说
            </Button>
            <Button
              type="primary"
              style={{ background: '#5F49E5' }}
              onClick={() => {
                // 去除后门中的 buildTime 的 key，如果 URL 中有 __buildTime__ 参数，则移除 该参数
                if (searchBuildTime) {
                  window.location.search = search.replace(/__buildTime__=([^&]+)/, `now=${now}`);
                  return;
                }

                // 生产环境下直接刷新当前页面即可
                if (isPro()) {
                  window.location.reload();
                  return;
                }
                // NOTE 非生产环境下，使用 broadcastSync 来进行刷新，它不光会刷新自己，同时也会刷新其他 tab 页面。
                BroadcastSync.query(VERSION_REFRESH_SYNC_CHANNEL).post(true);
              }}
            >
              立即刷新
            </Button>
          </Space>
        ),
        duration: null,
      });
    }
  };

  let timeout: any;
  return function () {
    clearTimeout(timeout);
    timeout = setTimeout(() => {
      notificationHandler.apply(null);
    }, wait || 200);
  };
};

/**
 * @description 版本更新通知的 hooks
 */
export const useChangeNotify = () => {
  useEffect(() => {
    // 获取一个 3 秒的防抖通知处理器
    const debounceNotificationHandler = getNotificationEventHandler(3 * 1000);

    // 绑定事件监听器，常规的用户操作行为会触发通知处理器
    // window.addEventListener('popstate', debounceNotificationHandler);
    // window.addEventListener('hashchange', debounceNotificationHandler);
    // window.addEventListener('visibilitychange', debounceNotificationHandler);
    // window.addEventListener(
    //   'click',
    //   (e) => e?.target?.tagName === 'A' && e?.target?.href && debounceNotificationHandler(),
    // );

    let timer = window.setInterval(debounceNotificationHandler, 1000 * 60 * 5); // 5 分钟确认一次项目是否已更新

    // 非生产环境下监听版本更新刷新的通知
    if (!isPro()) {
      BroadcastSync.query(VERSION_REFRESH_SYNC_CHANNEL).addEventListener(() => {
        window.location.reload();
      });
    }

    // 解绑事件监听器
    return () => {
      // window.removeEventListener('popstate', debounceNotificationHandler);
      // window.removeEventListener('hashchange', debounceNotificationHandler);
      // window.removeEventListener('visibilitychange', debounceNotificationHandler);
      // window.removeEventListener('click', debounceNotificationHandler);
      clearTimeout(timer);
    };
  }, []);
};
